import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import Grid from 'apollo-react/components/Grid';
import IconButton from 'apollo-react/components/IconButton';
import IconMenuButton from 'apollo-react/components/IconMenuButton';
import Loader from 'apollo-react/components/Loader';
import MenuButton from 'apollo-react/components/MenuButton';
import Tooltip from 'apollo-react/components/Tooltip';
import Typography from 'apollo-react/components/Typography';
import InfoIcon from 'apollo-react-icons/Info';
import Menu from 'apollo-react-icons/Menu';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import PublishVirtualEntityModal from './PublishVirtualEntityModal';
import SaveAsModal from './SaveAsModal';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';
import { HOME } from '../../constants/Urls';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { getToken } from '../../form-designer/selectors/common.selectors';
import { getSmartInsightApplicationURL } from '../../selectors/AuthSelectors';
import { getUITA } from '../../utils/commonUtils';
import { DATE_FORMAT_WITH_TIMESTAMP } from '../../utils/dateUtils';
import { getLinkToLegacyReportViewer } from '../../utils/smartInsightUtils';
import {
  deleteReport,
  exportReport,
  saveReport,
  setSelectedControlObject,
  setSelectedTabIndex,
} from '../actions/report-designer.actions';
import { REPORT_LAYOUT_ID } from '../constants/report-designer.constants';
import {
  getIsPublishVirtualEntityDisabled,
  getIsSaveDisabled,
  getReportInfo,
} from '../selectors/report-designer.selectors';

const useStyles = makeStyles({
  wrapper: {
    borderBottom: '1px solid #e9e9e9',
    padding: '20px 0px 20px 32px',
    '& ul': {
      marginLeft: '15px',
    },
  },
  reportInfoText: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'start',
    '@media (max-width: 1200px)': {
      display: 'none',
    },
  },
  reportInfoTextContainer: {
    marginRight: 55,
    margin: 5,
    display: 'flex',
    alignItems: 'start',
  },
  reportInfoTextTitleContainer: {
    marginRight: 10,
  },
  reportInfoToolTip: {
    display: 'none',
    '@media (max-width: 1200px)': {
      display: 'block',
    },
  },
  reportInfoTextTitle: {
    fontFamily: 'Proxima Nova',
    fontSize: 14,
    fontStyle: 'normal',
    fontStretch: 'normal',
    letterSpacing: 'normal',
    color: 'var(--black)',
    height: 21,
  },
  titleWrapper: {
    '@media (max-width: 1200px)': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  buttonsWrapper: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    '@media (max-width: 1200px)': {
      paddingRight: 28,
    },
  },
  hiddenMenuButton: {
    display: 'none',
    '@media (max-width: 1200px)': {
      display: 'block',
    },
  },
  marginRight: {
    marginRight: 15,
    '@media (max-width: 1200px)': {
      display: 'none',
    },
  },
  resetOverflow: {
    overflow: 'visible!important',
    minWidth: 'initial!important',
  },
});

const ReportHeader = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const translateString = useTranslation();

  const reportInfo = useSelector(getReportInfo);
  const isSaveDisabled = useSelector(getIsSaveDisabled);
  const isPublishVirtualEntityDisabled = useSelector(getIsPublishVirtualEntityDisabled);
  const smartInsightApplicationURL = useSelector(getSmartInsightApplicationURL);
  const token = useSelector(getToken);

  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpened] = useState(false);
  const [isSaveAsModalOpen, setIsSaveAsModalOpen] = useState(false);
  const [isPublishVirtualEntityModalOpen, setIsPublishVirtualEntityModalOpen] = useState(false);

  useEffect(() => {
    document.title =
      (reportInfo.reportKey && `Report Editor / ${reportInfo.reportKey}`) || 'Report Editor';
  }, [reportInfo.reportKey]);

  const redirectToPrevPage = () => {
    if (history.length > 1) {
      history.goBack();
    } else {
      history.push(`${HOME}`);
    }
  };

  const handleSaveButton = async () => {
    setIsLoading(true);
    await dispatch(saveReport({}));
    setIsLoading(false);
  };

  const handleCancelButton = () => {
    redirectToPrevPage();
  };

  const handleReportExport = async () => {
    setIsLoading(true);
    await dispatch(exportReport(reportInfo.reportId, reportInfo.reportKey));
    setIsLoading(false);
  };

  const handleDeleteReportConfirm = async () => {
    setIsDeleteModalOpened(false);
    setIsLoading(true);
    await dispatch(deleteReport({ redirectToPrevPage, id: reportInfo.reportId }));
    setIsLoading(false);
  };

  const showReportProperties = () => {
    batch(() => {
      dispatch(setSelectedTabIndex(0));
      dispatch(setSelectedControlObject(REPORT_LAYOUT_ID, false));
    });
  };

  const runReport = () => {
    window.open(
      getLinkToLegacyReportViewer(smartInsightApplicationURL, reportInfo.reportId, token),
      '_blank'
    );
  };

  const reportLastModifiedDate = reportInfo.reportLastModified
    ? moment(reportInfo.reportLastModified).format(DATE_FORMAT_WITH_TIMESTAMP)
    : ' ';

  const virtualEntityMobileInfo = reportInfo.virtualEntityName
    ? [{ title: `${translateString('Virtual Entity')}: ${reportInfo.virtualEntityName}` }]
    : [];

  const reportInfoArr = [
    { title: `${translateString('Report Name')}: ${reportInfo.reportKey}` },
    { title: `${translateString('Report Model')}: ${reportInfo.reportModelName}` },
    { title: `${translateString('Last Modified Date')}: ${reportLastModifiedDate}` },
    ...virtualEntityMobileInfo,
  ];

  const actionItems = [
    { text: translateString('Save As'), onClick: () => setIsSaveAsModalOpen(true) },
    {
      text: translateString('Delete'),
      onClick: () => setIsDeleteModalOpened(true),
    },
    {
      text: translateString('Export'),
      onClick: handleReportExport,
    },
    {
      text: translateString('Publish for Saved Searches'),
      onClick: () => setIsPublishVirtualEntityModalOpen(true),
      disabled: isPublishVirtualEntityDisabled,
    },
    {
      text: translateString('Report Properties'),
      onClick: showReportProperties,
    },
    {
      text: translateString('Run'),
      onClick: runReport,
    },
  ];

  const menuItems = [
    ...actionItems,
    {
      text: translateString('Cancel'),
      onClick: handleCancelButton,
    },
    {
      text: translateString('Save'),
      onClick: handleSaveButton,
      disabled: isSaveDisabled,
    },
  ];

  const deleteConfirmMessage = (
    <>
      {translateString('Do you really want to delete report')} <b>{reportInfo.reportKey}</b>
      {'?'}
    </>
  );

  return (
    <div className={classes.wrapper}>
      <Grid container>
        <Grid className={classes.titleWrapper} item xs={8}>
          <span className={classes.reportInfoToolTip}>
            {reportInfo && (
              <Tooltip extraLabels={reportInfoArr} placement="right" variant="light">
                <IconButton>
                  <InfoIcon color="primary" />
                </IconButton>
              </Tooltip>
            )}
          </span>
          <span className={classes.reportInfoText} ui-ta={getUITA('ReportInfo')}>
            <div className={classes.reportInfoTextContainer}>
              <div className={classes.reportInfoTextTitleContainer}>
                <Typography className={classes.reportInfoTextTitle}>
                  {`${translateString('Report Name')}:`}
                </Typography>
                <Typography className={classes.reportInfoTextTitle}>
                  {`${translateString('Last Modified Date')}:`}
                </Typography>
              </div>
              <div>
                <Typography className={classes.reportInfoTextTitle}>
                  {reportInfo.reportKey}
                </Typography>
                <Typography className={classes.reportInfoTextTitle}>
                  {reportLastModifiedDate}
                </Typography>
              </div>
            </div>
            <div className={classes.reportInfoTextContainer}>
              <div className={classes.reportInfoTextTitleContainer}>
                <Typography className={classes.reportInfoTextTitle}>
                  {`${translateString('Report Model')}:`}
                </Typography>
                {reportInfo.virtualEntityName && (
                  <Typography className={classes.reportInfoTextTitle}>
                    {`${translateString('Virtual Entity')}:`}
                  </Typography>
                )}
              </div>
              <div>
                <Typography className={classes.reportInfoTextTitle}>
                  {reportInfo.reportModelName}
                </Typography>
                {reportInfo.virtualEntityName && (
                  <Typography className={classes.reportInfoTextTitle}>
                    {reportInfo.virtualEntityName}
                  </Typography>
                )}
              </div>
            </div>
          </span>
        </Grid>
        <Grid className={classes.buttonsWrapper} item xs={4}>
          <IconMenuButton
            className={classes.hiddenMenuButton}
            menuItems={menuItems.filter((action) => !action.hidden)}
          >
            <Menu />
          </IconMenuButton>
          <MenuButton
            buttonText={translateString('Actions')}
            menuItems={actionItems.filter((action) => !action.hidden)}
            className={`${classes.marginRight} ${classes.resetOverflow}`}
          />
          <Button
            variant="text"
            className={`${classes.marginRight} ${classes.resetOverflow}`}
            onClick={handleCancelButton}
            ui-ta={getUITA('Editor Header Button', 'Cancel')}
          >
            {translateString('Cancel')}
          </Button>
          <Button
            variant="secondary"
            className={`${classes.marginRight} ${classes.resetOverflow}`}
            onClick={handleSaveButton}
            ui-ta={getUITA('Editor Header Button', 'Save')}
            disabled={isSaveDisabled}
          >
            {translateString('Save')}
          </Button>
          {isLoading && <Loader isInner />}
        </Grid>
      </Grid>
      {isSaveAsModalOpen && (
        <SaveAsModal
          isOpen={isSaveAsModalOpen}
          initialReportName={reportInfo.reportKey}
          handleClose={() => setIsSaveAsModalOpen(false)}
          setIsLoading={setIsLoading}
        />
      )}
      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        message={deleteConfirmMessage}
        onClose={() => setIsDeleteModalOpened(false)}
        onDeleteConfirm={handleDeleteReportConfirm}
      />
      {isPublishVirtualEntityModalOpen && (
        <PublishVirtualEntityModal
          handleClose={() => setIsPublishVirtualEntityModalOpen(false)}
          setIsLoading={setIsLoading}
        />
      )}
    </div>
  );
};

export default ReportHeader;
